// Write-back da origem do placement de um produto (tipicamente o
// IfcSite) ao próprio ficheiro IFC, com opção de compensar os
// placements FILHOS para as posições absolutas de tudo o resto não
// mudarem: a forma de "re-referenciar tudo ao IfcSite": o site passa a
// carregar as coordenadas e os filhos ficam com offsets pequenos.
//
// Segurança primeiro:
//   - Nunca reescrevemos IfcCartesianPoint/IfcAxis2Placement3D em
//     sítio: esses objetos podem ser PARTILHADOS por vários placements
//     (ex.: o ponto (0,0,0) usado em todo o lado). Em vez disso
//     cunhamos entidades novas e re-apontamos só a IfcLocalPlacement
//     em causa.
//   - Qualquer forma inesperada no STEP aborta com erro; a função é
//     transacional (ou devolve os bytes completos alterados, ou lança).
//   - Compensação de filhos respeita a rotação do placement do site
//     (Δ transformado para o frame local); placements com eixo Z
//     inclinado são recusados.

import { detectProjectMetresPerUnit } from './georefWrite'
import {
  bytesToLatin1,
  findEntityById,
  latin1ToBytes,
  splitTopLevelArgs,
  stepReal,
} from './stepText'

export interface ProductPlacementWriteResult {
  bytes: Uint8Array
  /** Origem antiga do placement, em unidades do ficheiro. */
  oldLocationFileUnits: [number, number, number]
  /** Origem nova, em unidades do ficheiro. */
  newLocationFileUnits: [number, number, number]
  /** Placements filhos compensados (0 sem compensação). */
  compensatedChildren: number
  projectMetresPerUnit: number
}

/** Reescreve a origem do ObjectPlacement de um produto (IfcSite,
 *  IfcBuilding, ...) nos bytes do IFC. `newLocationMetres` é a nova
 *  origem em METROS no referencial do PAI do placement. Com
 *  `compensateChildren`, os placements diretamente relativos ao do
 *  produto são ajustados por -Δ para nada mudar de posição absoluta. */
export function writeProductPlacement(
  bytes: Uint8Array,
  opts: {
    productExpressID: number
    newLocationMetres: [number, number, number]
    compensateChildren: boolean
  },
): ProductPlacementWriteResult {
  let text = bytesToLatin1(bytes)
  const factor = detectProjectMetresPerUnit(text)
  if (factor === null) {
    throw new Error(
      'Não foi possível determinar a unidade de comprimento do projeto ' +
        '(IfcSIUnit LENGTHUNIT); ficheiro não alterado.',
    )
  }

  // Produto → ObjectPlacement (argumento 5 de qualquer IfcProduct).
  const productLine = findEntityById(text, opts.productExpressID)
  if (!productLine) {
    throw new Error(`Entidade #${opts.productExpressID} não encontrada.`)
  }
  const productArgs = splitTopLevelArgs(productLine.args)
  const placementRef = (productArgs[5] ?? '').trim()
  if (!/^#\d+$/.test(placementRef)) {
    throw new Error(
      `${productLine.type} #${opts.productExpressID} não tem ObjectPlacement ` +
        'referenciável; nada a editar.',
    )
  }
  const placementId = Number(placementRef.slice(1))

  // IfcLocalPlacement(PlacementRelTo, RelativePlacement).
  const placementLine = findEntityById(text, placementId)
  if (!placementLine || placementLine.type !== 'IFCLOCALPLACEMENT') {
    throw new Error(
      `ObjectPlacement #${placementId} não é IFCLOCALPLACEMENT ` +
        `(${placementLine?.type ?? 'em falta'}); não suportado.`,
    )
  }
  const placementArgs = splitTopLevelArgs(placementLine.args)
  const axisRef = (placementArgs[1] ?? '').trim()
  if (!/^#\d+$/.test(axisRef)) {
    throw new Error(
      `IfcLocalPlacement #${placementId} sem RelativePlacement referenciável.`,
    )
  }
  const axisId = Number(axisRef.slice(1))

  // IfcAxis2Placement3D(Location, Axis, RefDirection).
  const axisLine = findEntityById(text, axisId)
  if (!axisLine || axisLine.type !== 'IFCAXIS2PLACEMENT3D') {
    throw new Error(
      `RelativePlacement #${axisId} não é IFCAXIS2PLACEMENT3D; não suportado.`,
    )
  }
  const axisArgs = splitTopLevelArgs(axisLine.args)
  const locRef = (axisArgs[0] ?? '').trim()
  const axisDirArg = (axisArgs[1] ?? '$').trim()
  const refDirArg = (axisArgs[2] ?? '$').trim()
  if (!/^#\d+$/.test(locRef)) {
    throw new Error(`IfcAxis2Placement3D #${axisId} sem Location.`)
  }
  const oldLoc = readCartesianPoint(text, Number(locRef.slice(1)))

  const newLocFile: [number, number, number] = [
    opts.newLocationMetres[0] / factor,
    opts.newLocationMetres[1] / factor,
    opts.newLocationMetres[2] / factor,
  ]
  const delta: [number, number, number] = [
    newLocFile[0] - oldLoc[0],
    newLocFile[1] - oldLoc[1],
    newLocFile[2] - oldLoc[2],
  ]

  // Rotação do placement (para compensar filhos no frame local).
  let thetaRad = 0
  if (opts.compensateChildren) {
    if (axisDirArg !== '$') {
      const axisDir = readDirection(text, axisDirArg)
      if (
        Math.abs(axisDir[0]) > 1e-9 ||
        Math.abs(axisDir[1]) > 1e-9 ||
        Math.abs((axisDir[2] ?? 1) - 1) > 1e-9
      ) {
        throw new Error(
          'O placement tem o eixo Z inclinado; a compensação de filhos ' +
            'só está suportada para placements verticais.',
        )
      }
    }
    if (refDirArg !== '$') {
      const refDir = readDirection(text, refDirArg)
      thetaRad = Math.atan2(refDir[1] ?? 0, refDir[0] ?? 1)
    }
  }

  // Alterações: cunhar ponto+eixo novos para o produto e re-apontar a
  // IfcLocalPlacement; opcionalmente o mesmo para cada filho direto.
  let maxId = 0
  const idRe = /#(\d+)\s*=/g
  for (let m = idRe.exec(text); m; m = idRe.exec(text)) {
    const v = Number(m[1])
    if (v > maxId) maxId = v
  }
  const nl = text.includes('\r\n') ? '\r\n' : '\n'
  const spanEdits: Array<{ start: number; end: number; repl: string }> = []
  let appended = ''
  let nextId = maxId + 1

  const mintPlacementRetarget = (
    lp: { start: number; end: number; type: string; args: string },
    lpId: number,
    location: [number, number, number],
    srcAxisDirArg: string,
    srcRefDirArg: string,
  ) => {
    const ptId = nextId++
    const axId = nextId++
    appended +=
      `#${ptId}=IFCCARTESIANPOINT((${stepReal(location[0])},${stepReal(location[1])},${stepReal(location[2])}));${nl}` +
      `#${axId}=IFCAXIS2PLACEMENT3D(#${ptId},${srcAxisDirArg},${srcRefDirArg});${nl}`
    const lpArgs = splitTopLevelArgs(lp.args)
    const rel = (lpArgs[0] ?? '$').trim()
    spanEdits.push({
      start: lp.start,
      end: lp.end,
      repl: `#${lpId}=IFCLOCALPLACEMENT(${rel},#${axId});`,
    })
  }

  mintPlacementRetarget(
    placementLine,
    placementId,
    newLocFile,
    axisDirArg,
    refDirArg,
  )

  // Filhos diretos: IfcLocalPlacement cujo PlacementRelTo é o placement
  // do produto. Compensação no frame local do produto: c' = c − R(−θ)·Δ.
  let compensated = 0
  if (opts.compensateChildren) {
    const cos = Math.cos(-thetaRad)
    const sin = Math.sin(-thetaRad)
    const dLocal: [number, number, number] = [
      delta[0] * cos - delta[1] * sin,
      delta[0] * sin + delta[1] * cos,
      delta[2],
    ]
    const childRe = /#(\d+)\s*=\s*IFCLOCALPLACEMENT\s*\(/g
    for (let m = childRe.exec(text); m; m = childRe.exec(text)) {
      const childId = Number(m[1])
      if (childId === placementId) continue
      const childLine = findEntityById(text, childId)
      if (!childLine) continue
      const cArgs = splitTopLevelArgs(childLine.args)
      if ((cArgs[0] ?? '').trim() !== `#${placementId}`) continue
      const cAxisRef = (cArgs[1] ?? '').trim()
      if (!/^#\d+$/.test(cAxisRef)) {
        throw new Error(
          `Placement filho #${childId} sem RelativePlacement; compensação abortada.`,
        )
      }
      const cAxisLine = findEntityById(text, Number(cAxisRef.slice(1)))
      if (!cAxisLine || cAxisLine.type !== 'IFCAXIS2PLACEMENT3D') {
        throw new Error(
          `Placement filho #${childId} com RelativePlacement não suportado; compensação abortada.`,
        )
      }
      const cAxisArgs = splitTopLevelArgs(cAxisLine.args)
      const cLocRef = (cAxisArgs[0] ?? '').trim()
      if (!/^#\d+$/.test(cLocRef)) {
        throw new Error(
          `Placement filho #${childId} sem Location; compensação abortada.`,
        )
      }
      const cOld = readCartesianPoint(text, Number(cLocRef.slice(1)))
      const cNew: [number, number, number] = [
        cOld[0] - dLocal[0],
        cOld[1] - dLocal[1],
        cOld[2] - dLocal[2],
      ]
      mintPlacementRetarget(
        childLine,
        childId,
        cNew,
        (cAxisArgs[1] ?? '$').trim(),
        (cAxisArgs[2] ?? '$').trim(),
      )
      compensated++
    }
  }

  // Aplicar edições de trás para a frente (offsets estáveis) e anexar
  // as entidades novas antes do ENDSEC.
  spanEdits.sort((a, b) => b.start - a.start)
  for (const e of spanEdits) {
    text = text.slice(0, e.start) + e.repl + text.slice(e.end)
  }
  const endsec = text.lastIndexOf('ENDSEC;')
  if (endsec < 0) {
    throw new Error('Ficheiro STEP sem ENDSEC; estrutura inesperada.')
  }
  text = text.slice(0, endsec) + appended + text.slice(endsec)

  return {
    bytes: latin1ToBytes(text),
    oldLocationFileUnits: oldLoc,
    newLocationFileUnits: newLocFile,
    compensatedChildren: compensated,
    projectMetresPerUnit: factor,
  }
}

/** Lê as coordenadas de um IFCCARTESIANPOINT (2D vira z=0). */
function readCartesianPoint(
  text: string,
  id: number,
): [number, number, number] {
  const line = findEntityById(text, id)
  if (!line || line.type !== 'IFCCARTESIANPOINT') {
    throw new Error(
      `Location #${id} não é IFCCARTESIANPOINT (${line?.type ?? 'em falta'}).`,
    )
  }
  const inner = line.args.trim()
  const m = inner.match(/^\(([^)]*)\)$/)
  if (!m) {
    throw new Error(`IFCCARTESIANPOINT #${id} com forma inesperada.`)
  }
  const nums = m[1].split(',').map((s) => Number(s.trim()))
  if (nums.some((n) => !Number.isFinite(n)) || nums.length < 2) {
    throw new Error(`IFCCARTESIANPOINT #${id} com coordenadas ilegíveis.`)
  }
  return [nums[0], nums[1], nums[2] ?? 0]
}

/** Lê os DirectionRatios de um argumento '#id' de IFCDIRECTION. */
function readDirection(text: string, refArg: string): number[] {
  if (!/^#\d+$/.test(refArg)) {
    throw new Error(`Direção "${refArg}" com forma inesperada.`)
  }
  const line = findEntityById(text, Number(refArg.slice(1)))
  if (!line || line.type !== 'IFCDIRECTION') {
    throw new Error(
      `Direção ${refArg} não é IFCDIRECTION (${line?.type ?? 'em falta'}).`,
    )
  }
  const m = line.args.trim().match(/^\(([^)]*)\)$/)
  if (!m) {
    throw new Error(`IFCDIRECTION ${refArg} com forma inesperada.`)
  }
  return m[1].split(',').map((s) => Number(s.trim()))
}
